import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Header from './Header';
import Footer from './Footer';
import Aside from './Aside';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useNavigate, useLocation } from "react-router-dom";

const fetchSubscriptionData = async () => {
    const authData = localStorage.getItem('auth');
    if (!authData) throw new Error("User not authenticated");

    let parsedAuth;
    try {
        parsedAuth = JSON.parse(authData);
    } catch (error) {
        throw new Error("Invalid authentication data");
    }

    const { token, id } = parsedAuth;
    const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/plans`,
        {
            params: { user_id: id, guest: false },
            headers: {
                Authorization: `Bearer ${token}`,
            },
        }
    );

    if (response.data.status === 200) {
        return response.data;
    } else if (response.data.status === 401) {
        throw new Error("Unauthorized");
    } else {
        throw new Error(response.data.message || "Failed to fetch data");
    }
};

const ManageSubscription = () => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const location = useLocation();
    const [isLoadingFree, setIsLoadingFree] = useState(false);
    const [isLoadingBasic, setIsLoadingBasic] = useState(false);
    const [isLoadingPro, setIsLoadingPro] = useState(false);
    const [checkTemp, setCheckTemp] = useState(false);
    const [editLoading, setEditLoading] = useState(false);

    const { data: subscriptionData, error } = useQuery({
        queryKey: ["subscriptionData", "/plans"],
        queryFn: fetchSubscriptionData,
        staleTime: Infinity, // Data never becomes stale
        cacheTime: Infinity, // Keep cache indefinitely
        refetchOnWindowFocus: false, // Don't refetch on tab switch
        refetchOnMount: true, // Fetch when component mounts
        keepPreviousData: true, // Keep previous data until new data arrives
        enabled: !!localStorage.getItem("auth") && location.pathname === "/plans", // Only fetch when on /plans
    });

    useEffect(() => {
        const timer = setTimeout(() => {
            const link = document.createElement('link');
            link.href = './assets/plugins/global/plugins.bundle.css';
            link.rel = 'stylesheet';
            document.head.appendChild(link);
        }, 100);
        return () => clearTimeout(timer);
    }, [location.pathname]);

    useEffect(() => {
        const processCheckout = async () => {
            const searchParams = new URLSearchParams(location.search);
            const sessionId = searchParams.get("session_id");
            const action = searchParams.get("action");

            if (sessionId && action) {
                let updatedPathname = location.pathname;
                if (updatedPathname.endsWith("/")) {
                    updatedPathname = updatedPathname.slice(0, -1);
                }
                navigate(updatedPathname, { replace: true });

                if (action === "success") {
                    setCheckTemp(true);
                    try {
                        const authData = localStorage.getItem("auth");
                        const parsedAuth = authData ? JSON.parse(authData) : null;
                        const token = parsedAuth?.token || null;
                        const id = parsedAuth?.id || null;

                        if (!token || !id) {
                            throw new Error("User is not authenticated.");
                        }

                        const apiUrl = `${process.env.REACT_APP_API_URL}/api/subscription/success`;
                        const response = await axios.post(
                            apiUrl,
                            {
                                _token: token,
                                user_id: id,
                                session_id: sessionId,
                                app_url: `${process.env.REACT_APP_BASE_URL}/plans`
                            },
                            {
                                headers: {
                                    Authorization: `Bearer ${token}`,
                                },
                            }
                        );

                        if (response.data.status === 200) {
                            Swal.fire({
                                text: response.data.message,
                                icon: 'success',
                                title: 'Success!',
                                confirmButtonText: 'Ok',
                                allowOutsideClick: false,
                            });
                            queryClient.invalidateQueries(["subscriptionData"]);
                        } else if (response.data.status === 422) {
                            Swal.fire({
                                text: response.data.message,
                                icon: 'error',
                                title: 'Error!',
                                confirmButtonText: 'Ok',
                                allowOutsideClick: false,
                            });
                        } else if (response.data.status === 401) {
                            navigate("/login");
                        }
                    } catch (err) {
                        console.error("Error during checkout:", err);
                        Swal.fire({
                            text: "Failed to process checkout. Please try again.",
                            icon: 'error',
                            title: 'Error!',
                            confirmButtonText: 'Ok'
                        });
                    } finally {
                        setCheckTemp(false);
                    }
                } else {
                    window.location.href = "/plans";
                }
            }
        };

        processCheckout();
    }, [location.search, navigate, queryClient]);

    const handlePlanSelection = (plan, freeTrail) => {
        switch (plan.prod_name) {
            case 'Free':
                handleActivePlan(plan, freeTrail);
                break;
            case 'Basic':
                handleBasicPlan(plan, freeTrail);
                break;
            case 'Pro':
                handleProPlan(plan, freeTrail);
                break;
            default:
                console.log('Unknown plan selected');
        }
    };

    const handleActivePlan = async (plan, freeTrail) => {
        Swal.fire({
            title: subscriptionData?.plans[0]?.prod_name || 'Plan Details',
            icon: 'warning',
            html: `
                <p className="custom-text">No credit card required</p>
                <div className="custom-price3">
                    <p className="custom-price1">${plan.d_currency}</p>
                    <p className="custom-price">${(plan.amount / 100).toFixed(2)}</p>
                    <p className="custom-price2">/ ${plan.interval}</p>
                </div>
            `,
            showCancelButton: true,
            cancelButtonText: 'Cancel',
            confirmButtonText: 'Subscribe',
            customClass: {
                title: 'custom-title',
                confirmButton: 'custom-confirm-button',
                cancelButton: 'custom-cancel-button',
                popup: 'custom-popup',
            },
            preConfirm: () => handleCheckout(plan, freeTrail),
        }).then((result) => {
            if (result.dismiss === Swal.DismissReason.cancel) {
            }
        });
    };

    const handleCheckout = async (plan, freeTrail) => {
        const authData = localStorage.getItem('auth');
        const token = authData ? JSON.parse(authData).token : null;
        const user_id = authData ? JSON.parse(authData).id : null;
        const apiUrl = `${process.env.REACT_APP_API_URL}/api/subscriptions/checkout`;
        const slug = plan.prod_slug;
        const planid = plan.id;
        const free_trail = `${freeTrail.status}`;

        if (slug === 'free') setIsLoadingFree(true);
        if (slug === 'basic') setIsLoadingBasic(true);
        if (slug === 'pro') setIsLoadingPro(true);

        let requestBody = {
            plan_id: planid,
            free_trail: free_trail,
            user_id: user_id,
            guest: false,
            'app_url': `${process.env.REACT_APP_BASE_URL}/plans`
        };

        if (slug === 'free') {
            requestBody['free_subscription'] = true;
        }

        try {
            const response = await axios.post(apiUrl, requestBody, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (response.data.status === 200) {
                if (slug === 'free') {
                    Swal.fire({
                        text: response.data.message,
                        icon: 'success',
                        title: 'Success!',
                        confirmButtonText: 'Ok'
                    }).then(() => {
                        queryClient.invalidateQueries(["subscriptionData"]);
                    });
                } else {
                    window.location.href = response.data.checkout.url;
                }
            } else if (response.data.status === 422) {
                Swal.fire({
                    text: response.data.message,
                    icon: 'error',
                    title: 'Error!',
                    confirmButtonText: 'Ok'
                });
            } else if (response.data.status === 401) {
                localStorage.removeItem("auth");
                navigate('/login');
            }
        } catch (error) {
            console.log(error);
        } finally {
            if (slug === 'free') setIsLoadingFree(false);
            if (slug === 'basic') setIsLoadingBasic(false);
            if (slug === 'pro') setIsLoadingPro(false);
        }
    };

    const handleBasicPlan = (plan, freeTrail) => handleCheckout(plan, freeTrail);
    const handleProPlan = (plan, freeTrail) => handleCheckout(plan, freeTrail);

    const handleeditbilling = async () => {
        setEditLoading(true);
        try {
            const authData = localStorage.getItem("auth");
            const parsedAuth = authData ? JSON.parse(authData) : null;
            const token = parsedAuth?.token || null;
            const id = parsedAuth?.id || null;

            if (!token || !id) {
                throw new Error("User is not authenticated.");
            }

            const apiUrl = `${process.env.REACT_APP_API_URL}/api/billing/edit-billing-invoice/${token}`;
            const response = await axios.post(apiUrl, { _token: token, user_id: id }, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (response.data.status === 200 && response.data.url) {
                window.location.href = response.data.url;
            } else if (response.data.status === 422) {
                Swal.fire({
                    text: response.data.message || "Validation error.",
                    icon: 'error',
                    title: 'Error!',
                    confirmButtonText: 'Ok'
                });
            } else if (response.data.status === 401) {
                navigate("/login");
            }
        } catch (err) {
            console.error("Error in editBill:", err);
            Swal.fire({
                text: "Failed to fetch billing data. Please try again.",
                icon: 'error',
                title: 'Error!',
                confirmButtonText: 'Ok'
            });
        } finally {
            setEditLoading(false);
        }
    };

    const moveplans = () => {
        const targetElement = document.getElementById('#choose-plans-card');
        if (targetElement) {
            targetElement.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        <>
            {checkTemp ? (
                <div className="container " style={{ marginTop: '70px' }}>
                    <div className="row justify-content-center">
                        <div className="col-md-5 mt-5">
                            <div className="message-box _success shadow-lg">
                                <div className="payment-loader mb-3">
                                    <div className="pad">
                                        <div className="chip"></div>
                                        <div className="line line1"></div>
                                        <div className="line line2"></div>
                                    </div> </div>
                                <h3 className="mt-5"> Please wait while payment is loading </h3>
                                <p> Thank you for your payment. we will
                                    be in contact with more details shortly </p>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div>
                    <Header />
                    <div style={{ display: 'flex' }}>
                        <div style={{ zIndex: '101', background: 'white', boxShadow: '0 .8rem 1rem rgba(0, 0, 0, .15)', position: 'fixed', height: '100vh' }}>
                            <Aside />
                        </div>
                        <div className='mainstylle'>
                            <section className="generate-main paddtop">
                                <div className="container py-4 px-4">
                                    <h1 className="text-center fs-4hx fw-bold">Manage Subscription</h1>
                                    <p className="text-center fs-4">Choose your plan that works for you.</p>

                                    {subscriptionData ? (
                                        subscriptionData?.current_subscription != '' ? (
                                            <>
                                                <div className="row your-plans card rounded-4 pt-10 pb-5 px-3 current-plan">
                                                    <div className="current-subscription mb-3">
                                                        <div className="d-flex align-items-center mb-3">
                                                            <h1 className="text-left fw-bold pb-3 text-dark w-50 flex-grow-1">
                                                                Your {subscriptionData.current_subscription.plan.prod_name} Plan
                                                            </h1>
                                                            <p className="fw-semibold fs-4 rounded-pill py-1 px-2"
                                                                style={{
                                                                    color: subscriptionData.current_subscription.status === "active" ||
                                                                        subscriptionData.current_subscription.status === "trialing"
                                                                        ? "rgb(66, 167, 133)"
                                                                        : "rgb(193, 31, 54)"
                                                                }}>
                                                                <i className="bi bi-patch-check-fill pe-2"></i>
                                                                {subscriptionData.current_subscription.status}
                                                            </p>
                                                        </div>
                                                        <div className="row">
                                                            {/* Card 1 */}
                                                            <div className="col-md-4 mt-3 free">
                                                                <div className="card shadow rounded-4 card-xxl-stretch h-100">
                                                                    <div className="card-title mt-6 mb-3 ms-5">
                                                                        <h2>Current Plan</h2>
                                                                    </div>
                                                                    <div className="card-body">
                                                                        <div className="d-flex align-items-center mb-3">
                                                                            <span className="fw-bold text-primary fs-6 flex-grow-1 pe-3">Plans</span>
                                                                            <span className="fw-semibold fs-6 pe-3">
                                                                                <h3>{subscriptionData.current_subscription.plan.prod_name}</h3>
                                                                            </span>
                                                                        </div>
                                                                        <div className="d-flex align-items-center mb-4">
                                                                            <span className="fw-bold text-primary fs-6 flex-grow-1 pe-3">Price</span>
                                                                            <span className="fw-semibold fs-6 pe-3">
                                                                                {subscriptionData.current_subscription.d_currency}
                                                                                {(subscriptionData.current_subscription.plan.amount / 100).toFixed(2)}/
                                                                                {subscriptionData.current_subscription.plan.interval}
                                                                            </span>
                                                                        </div>
                                                                        <div className="d-flex align-self-center  plan-info">
                                                                            <div className="w-100 mt-2">
                                                                                <p class="col-md-12 col-12 ">To view more details about the plan, click the button below.</p>
                                                                                <div className="d-flex align-items-center ">
                                                                                    <a onClick={moveplans} className="col-md-12 mb-3  col-12 btn fw-semibold  py-3 px-6 float-end edit-billing-btn btn-theme btn-lg btn-shadow">
                                                                                        See More
                                                                                    </a>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Card 2 */}
                                                            <div className="col-md-4 mt-3 free">
                                                                <div className="card shadow rounded-4 card-xxl-stretch h-100">
                                                                    <div className="card-title mt-6 mb-3 ms-5">
                                                                        <h2>Billing & Payment</h2>
                                                                    </div>
                                                                    <div className="card-body">
                                                                        <div className="d-flex align-items-center mb-3">
                                                                            <span className="fw-bold text-primary fs-6 flex-grow-1 pe-3">Next Billing Date</span>
                                                                            <span className="fw-semibold fs-6 pe-3">
                                                                                {subscriptionData.current_subscription.next_billing_time || (
                                                                                    <span style={{ color: "red" }}>Not found</span>
                                                                                )}
                                                                            </span>
                                                                        </div>
                                                                        <div className="d-flex align-items-center mb-5">
                                                                            <span className="fw-bold text-primary fs-6 flex-grow-1 pe-3">Plan expire on</span>
                                                                            <span className="fw-semibold fs-6 pe-3">
                                                                                {subscriptionData.current_subscription.next_billing_time || (
                                                                                    <span style={{ color: "red" }}>Not found</span>
                                                                                )}
                                                                            </span>
                                                                        </div>
                                                                        <div className="mt-6">
                                                                            <p className="col-md-12 col-12">Cancel at ANYTIME with NO commitments.</p>
                                                                        </div>
                                                                        <a disabled={editLoading} onClick={handleeditbilling}
                                                                            className="col-md-12 mb-3 col-12 btn fw-semibold py-3 px-6 float-end edit-billing-btn btn-theme btn-lg btn-shadow">
                                                                            {editLoading ? 'Processing...' : 'Edit Billing'}
                                                                        </a>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Card 3 */}
                                                            <div className="col-md-4 mt-3 free">
                                                                <div className="card shadow rounded-4 card-xxl-stretch h-100">
                                                                    <div className="card-title mt-6 mb-3 ms-5">
                                                                        <h2>Usage Details</h2>
                                                                    </div>
                                                                    <div className="card-body">
                                                                        <div className="d-flex align-items-center mb-3">
                                                                            <span className="fw-bold text-primary fs-6 flex-grow-1 pe-3">Daily captions remaining</span>
                                                                            <span className="fw-semibold fs-6 pe-3">
                                                                                ({subscriptionData.current_subscription.total_captions}/{subscriptionData.current_subscription.plan.access.no_of_captions_allow})
                                                                            </span>
                                                                        </div>
                                                                        <div className="d-flex align-items-center mb-3">
                                                                            <span className="fw-bold text-primary fs-6 flex-grow-1 pe-3">Daily images remaining</span>
                                                                            <span className="fw-semibold fs-6 pe-3">
                                                                                ({subscriptionData.current_subscription.total_images}/{subscriptionData.current_subscription.plan.access.no_of_image_allow})
                                                                            </span>
                                                                        </div>
                                                                        <div className="d-flex align-items-center mb-3">
                                                                            <span className="fw-bold text-primary fs-6 flex-grow-1 pe-3">Daily avatars remaining</span>
                                                                            <span className="fw-semibold fs-6 pe-3">
                                                                                ({subscriptionData.current_subscription.total_avatars}/{subscriptionData.current_subscription.plan.access.no_of_avatars_allow})
                                                                            </span>
                                                                        </div>
                                                                        <div className="d-flex align-items-center mb-4 pb-1">
                                                                            <span
                                                                                className={`fw-bold fs-6 flex-grow-1 pe-3 ${subscriptionData.current_subscription.plan.access.no_of_videos_allow === 0
                                                                                    ? "text-muted"
                                                                                    : "text-primary"
                                                                                    }`}
                                                                            >
                                                                                Daily videos remaining{" "}
                                                                                <i
                                                                                    className="fa fa-info-circle text-black"
                                                                                    data-bs-toggle="tooltip"
                                                                                    style={{ cursor: "pointer" }}
                                                                                    title="Available only for Pro Plan"
                                                                                ></i>
                                                                            </span>
                                                                            <span className={`fw-semibold fs-6 pe-3 ${subscriptionData.current_subscription.plan.access.no_of_videos_allow === 0
                                                                                ? "text-muted"
                                                                                : ""
                                                                                }`}>
                                                                                ({subscriptionData.current_subscription.total_videos}/
                                                                                {subscriptionData.current_subscription.plan.access.no_of_videos_allow})
                                                                            </span>
                                                                        </div>

                                                                        <a onClick={moveplans} className="col-md-12 mb-3 col-12 btn fw-semibold py-3 px-6 float-end edit-billing-btn btn-theme btn-lg btn-shadow">
                                                                            Change Plan
                                                                        </a>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="alert alert-danger subscription-plan-alert-msg mt-3">
                                                    <strong>Note:-</strong> Your current subscription will automatically be cancelled after you buy a new subscription plan.
                                                </div>
                                            </>
                                        ) : (
                                            <h4></h4>
                                        )
                                    ) : (
                                        <div className="col-md-12 mt-3 mb-4">
                                            <div className="card shadow-sm rounded-4 card-xxl-stretch h-100">
                                                <div className="card-body row align-items-center">
                                                    <div className="col-md-4 mt-3 free  ">
                                                        <div className="card shadow rounded-4 card-xxl-stretch h-100">
                                                            <div className="animated-card animated-card1"></div>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-4 mt-3 free  ">
                                                        <div className="card shadow rounded-4 card-xxl-stretch h-100">
                                                            <div className="animated-card animated-card1"></div>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-4 mt-3 free  ">
                                                        <div className="card shadow rounded-4 card-xxl-stretch h-100">
                                                            <div className="animated-card animated-card1"></div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Subscription Plans */}
                                    <div className="row your-plans card rounded-4 pb-5 px-3 current-plan">
                                        <div className="current-subscription mb-3 pt-10" id="#choose-plans-card">
                                            <div className="mb-3 text-center">
                                                <h1 className="fs-3hx fw-bold mb-5">Choose Your Plan</h1>
                                                <p className="fw-semibold fs-5">
                                                    If you need more info about our pricing, please check{" "}
                                                    <a href="#adsa" className="link-primary fw-bold" style={{ cursor: 'pointer' }}>Pricing Guidelines.</a>
                                                </p>
                                            </div>
                                            <div className="row plans subscription-plan">
                                                <div className="get-stripe-plans mx-0 px-0">
                                                    <div className="row g-10">
                                                        {subscriptionData?.plans?.length > 0 ? (
                                                            subscriptionData.plans.map((plan) => (
                                                                <div className="col-xl-4 col-md-4 col-12 free mt-5" key={plan.id}>
                                                                    <div className={`card rounded-4 card-xxl-stretch h-100 allplans ${plan.is_active.submit_btn_text === 'Active' ? 'active-plan-card' : ''}`}>
                                                                        <div className="card-title mt-6 text-center">
                                                                            <h2 className="text-dark fw-bolder plan-nature">{plan.prod_name}</h2>
                                                                            {plan.prod_name?.toLowerCase() === 'free' && (
                                                                                <div className="text-center">
                                                                                    <span className="fw-bold text2-muted">No credit card required</span>
                                                                                    <div className="text-center fw-bold text-primary">Serious Value for Individuals</div>
                                                                                </div>
                                                                            )}
                                                                            {plan.prod_name?.toLowerCase() === 'basic' && (
                                                                                <div className="text-center">
                                                                                    <span className="text-center fw-bold text-primary">Amazing powerpack for Influencers</span>
                                                                                    <div className="text-center mt-3">
                                                                                        <strike className="mb-2 fs-3 text2-muted fw-bold">
                                                                                            {plan.d_currency}{(plan.amount * 2 / 100).toFixed(2)}/
                                                                                        </strike>
                                                                                        <span style={{ textDecoration: 'line-through' }} className="fs-6 text2-muted">{plan.interval}</span>
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                            {plan.prod_name?.toLowerCase() === 'pro' && (
                                                                                <div className="text-center">
                                                                                    <span className="text-center fw-bold text-primary">Ultimate Professional for Business</span>
                                                                                    <div className="text-center mt-3">
                                                                                        <strike className="mb-2 fs-3 text2-muted fw-bold">
                                                                                            {plan.d_currency}{(plan.amount * 2 / 100).toFixed(2)}/
                                                                                        </strike>
                                                                                        <span style={{ textDecoration: 'line-through' }} className="fs-6 text2-muted">{plan.interval}</span>
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        <div className="card-body" style={{ paddingTop: '0px' }}>
                                                                            <div style={{ gap: "2px", alignItems: "baseline" }} className="d-flex justify-content-center text-center">
                                                                                <span className="mb-2 fs-2x text-primary">{plan.d_currency}</span>
                                                                                <span className="fs-3x fw-bold text-primary">{(plan.amount / 100).toFixed(2)}</span>
                                                                                <span className="fs-7 fw-semibold opacity-50">/{plan.interval}</span>
                                                                            </div>
                                                                            <div className="w-100 mb-10" style={{ textAlign: 'left' }}>
                                                                                {plan.descriptions.map((message, index) => (
                                                                                    <div key={index} className={`d-flex align-items-center mb-3 ${message.flag === "cross-circle" ? "text2-muted" : ""}`}>
                                                                                        <i className={`ki-outline fs-2 ${message.flag === "cross-circle" ? "text2-muted" : "text-primary"} ki-${message.flag}`} style={{ lineHeight: '1' }}></i>
                                                                                        <span className="ms-3 fw-semibold fs-6 flex-grow-1" dangerouslySetInnerHTML={{ __html: message.message }}></span>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                            <div className="text-center">
                                                                                <button
                                                                                    disabled={plan.is_active.submit_btn_text === 'Active' || isLoadingFree || isLoadingBasic || isLoadingPro}
                                                                                    className={`btn btn-theme btn-shadow text-center ${plan.is_active.submit_btn_text === 'Active' ? 'active-plan' : ''}`}
                                                                                    onClick={() => handlePlanSelection(plan, subscriptionData?.freeTrail)}>
                                                                                    {plan.prod_name === 'Free' && isLoadingFree ? 'Processing...' :
                                                                                        plan.prod_name === 'Basic' && isLoadingBasic ? 'Processing...' :
                                                                                            plan.prod_name === 'Pro' && isLoadingPro ? 'Processing...' :
                                                                                                plan.is_active.submit_btn_text}
                                                                                    <i className="ps-1 bi bi-rocket-takeoff"></i>
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <div className="col-12 text-center">
                                                                <h4>No Subscription Plans found</h4>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </section>
                            <Footer />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ManageSubscription;